import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link, Paper, Stack, TextField, Typography } from "@mui/material";
import { ArrowBackRounded, EmailOutlined, LockOutlined, PlayCircleOutlineRounded, Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { authErrorMessage, normalizeEmail, requestPasswordReset } from "../service/authService";

const fieldStyle = {
  "& .MuiInputBase-root": { minHeight: 58, borderRadius: 2, fontSize: 18, bgcolor: "#fff" },
  "& .MuiInputLabel-root": { fontSize: 17 },
  "& .MuiFormHelperText-root": { fontSize: 15, ml: 0, mt: 1 },
};

const LoginPage = () => {
  const { register, handleSubmit, getValues, formState: { errors } } = useForm({ shouldFocusError: true });
  const { login, sessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetMessage, setResetMessage] = useState("");

  const onSubmit = async ({ username, password }) => {
    if (loading) return;
    setSubmitError("");
    setLoading(true);
    try {
      await login({ username: normalizeEmail(username), password });
      const destination = location.state?.from;
      navigate(destination?.pathname?.startsWith("/dashboard") ? destination.pathname : "/dashboard", { replace: true });
    } catch (error) {
      setSubmitError(authErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const openReset = () => {
    setResetEmail(normalizeEmail(getValues("username") || ""));
    setResetError("");
    setResetOpen(true);
  };

  const sendReset = async (event) => {
    event.preventDefault();
    if (resetLoading) return;
    const email = normalizeEmail(resetEmail);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setResetError("Enter a valid email address so we can send the link.");
      return;
    }
    setResetError("");
    setResetLoading(true);
    try {
      await requestPasswordReset(email);
      setResetMessage("If that email has an account, we sent a password reset link. Check your inbox and spam folder.");
      setResetOpen(false);
    } catch (error) {
      setResetError(authErrorMessage(error, "reset"));
    } finally {
      setResetLoading(false);
    }
  };

  const openHelpGuide = () => window.dispatchEvent(new Event("rustic:open-help"));

  return (
    <Box component="main" id="main-content" sx={{ minHeight: "100svh", pt: { xs: 12, md: 15 }, pb: 7, px: 2, bgcolor: "#F7F3EC" }}>
      <Box sx={{ maxWidth: 1120, mx: "auto", display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, .86fr) minmax(0, 1.14fr)" }, gap: { xs: 3, md: 8 }, alignItems: "center" }}>
        <Box sx={{ maxWidth: 450, mx: { xs: "auto", md: 0 }, width: "100%", order: { xs: 2, md: 1 } }}>
          <Button onClick={() => navigate("/home")} startIcon={<ArrowBackRounded />} sx={{ color: "#244A38", minHeight: 48, px: 0, fontSize: 16 }}>
            Back to home
          </Button>
          <Typography component="h1" sx={{ mt: 2, fontFamily: '"DM Serif Display", Georgia, serif', fontSize: { xs: 42, sm: 50, md: 58 }, lineHeight: 1.07, color: "#18241D" }}>
            Welcome back to Rustic World
          </Typography>
          <Typography sx={{ mt: 2, fontSize: 19, lineHeight: 1.55, color: "#39483E" }}>
            Sign in to browse products, place an order and check your orders.
          </Typography>
          <Box sx={{ mt: 4, p: 3, bgcolor: "#E9EDE5", borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 17, color: "#18241D" }}>New to Rustic World?</Typography>
            <Typography sx={{ mt: 1, fontSize: 16, lineHeight: 1.5 }}>Create an account to start shopping.</Typography>
            <Button variant="outlined" onClick={() => navigate("/home#signup-section")} sx={{ mt: 2, minHeight: 50, borderWidth: 2, fontSize: 16 }}>Create an account</Button>
          </Box>
          <Button
            onClick={openHelpGuide}
            startIcon={<PlayCircleOutlineRounded />}
            sx={{ mt: 2.5, minHeight: 52, px: 0, fontSize: 17, fontWeight: 800 }}
          >
            Watch the step-by-step video
          </Button>
        </Box>

        <Paper elevation={0} sx={{ width: "100%", maxWidth: 520, mx: "auto", p: { xs: 3, sm: 4.5 }, borderRadius: 3, border: "1px solid #C9D3C7", boxShadow: "0 18px 48px rgba(24,36,29,.09)", bgcolor: "#FFFDF8", order: { xs: 1, md: 2 } }}>
          <Typography component="h2" sx={{ fontSize: 27, fontWeight: 800, color: "#18241D" }}>Sign in</Typography>
          <Typography sx={{ mt: .5, mb: 2.5, fontSize: 16, color: "#39483E" }}>Enter the email and password for your account.</Typography>
          {location.state?.registered && <Alert severity="success" role="status" sx={{ mb: 2, fontSize: 16 }}>Your account is ready. Sign in with the email and password you just created.</Alert>}
          {sessionExpired && <Alert severity="info" role="status" sx={{ mb: 2, fontSize: 16 }}>Your session ended. Sign in again to continue shopping.</Alert>}
          {submitError && <Alert severity="error" role="alert" sx={{ mb: 2, fontSize: 16 }}>{submitError}</Alert>}
          {resetMessage && <Alert severity="success" role="status" sx={{ mb: 2, fontSize: 16 }}>{resetMessage}</Alert>}
          <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2.5}>
              <TextField label="Email address" type="email" fullWidth autoComplete="username" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                {...register("username", { required: "Enter your email address.", pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter an email address like name@example.com." } })}
                error={!!errors.username} helperText={errors.username?.message} sx={fieldStyle}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined aria-hidden="true" /></InputAdornment> }} />
              <TextField label="Password" fullWidth type={showPassword ? "text" : "password"} autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                {...register("password", { required: "Enter your password." })}
                error={!!errors.password} helperText={errors.password?.message} sx={fieldStyle}
                InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlined aria-hidden="true" /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
              <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ minHeight: 58, fontSize: 18, fontWeight: 800 }}>
                {loading ? <><CircularProgress size={22} color="inherit" sx={{ mr: 1.5 }} /> Signing in…</> : "Sign in"}
              </Button>
            </Stack>
          </Box>
          <Button onClick={openReset} sx={{ mt: 2, px: 0, minHeight: 48, fontSize: 16, fontWeight: 700 }}>Forgot your password?</Button>
          {resetOpen && (
            <Box component="form" onSubmit={sendReset} sx={{ mt: 2, pt: 2.5, borderTop: "1px solid #D5DED2" }}>
              <Typography component="h3" sx={{ fontWeight: 800, fontSize: 20 }}>Reset your password</Typography>
              <Typography sx={{ mt: .5, mb: 2, fontSize: 16 }}>We'll email a link to the address on your account.</Typography>
              {resetError && <Alert severity="error" role="alert" sx={{ mb: 2, fontSize: 16 }}>{resetError}</Alert>}
              <TextField label="Account email address" type="email" value={resetEmail} onChange={(event) => setResetEmail(event.target.value)} fullWidth autoComplete="email" inputMode="email" autoCapitalize="none" sx={fieldStyle} />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" disabled={resetLoading} sx={{ minHeight: 52, fontSize: 16 }}>{resetLoading ? "Sending…" : "Email me a reset link"}</Button>
                <Button type="button" onClick={() => setResetOpen(false)} disabled={resetLoading} sx={{ minHeight: 52, fontSize: 16 }}>Cancel</Button>
              </Stack>
            </Box>
          )}
          <Typography sx={{ mt: 3, fontSize: 15, lineHeight: 1.5, color: "#39483E" }}>
            Need help? <Link href="tel:+15209871861" sx={{ fontWeight: 700 }}>Call Rustic World: (520) 987-1861</Link>
          </Typography>
          <Button
            onClick={openHelpGuide}
            startIcon={<PlayCircleOutlineRounded />}
            variant="outlined"
            fullWidth
            sx={{ mt: 2, minHeight: 54, borderWidth: 2, fontSize: 16 }}
          >
            Show me how to use this website
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginPage;
